import { Property, RoomType, OTAChannel, PricingRule, Event, AvailabilityRule } from '@/types/revenue-manager';

// ============ Properties ============
export const mockPropertiesData: Property[] = [
  {
    id: '1',
    name: 'Grand Plaza Hotel',
    location: 'Downtown',
    basePrice: 150,
    roomCount: 100,
  },
  {
    id: '2',
    name: 'Seaside Resort',
    location: 'Beach District',
    basePrice: 200,
    roomCount: 75,
  },
  {
    id: '3',
    name: 'Mountain View Lodge',
    location: 'Highland Area',
    basePrice: 180,
    roomCount: 50,
  },
];

// ============ Room Types ============
export const mockRoomTypes: RoomType[] = [
  { id: 'rt1', name: 'HUT', adults: 4, children: 2, pricePerNight: 2000 },
  { id: 'rt2', name: '20 Beded Dormitory Tent', adults: 20, children: 5, pricePerNight: 6000 },
  { id: 'rt3', name: 'Doble Beded Non Attached Tent', adults: 3, children: 2, pricePerNight: 1600 },
  { id: 'rt4', name: '4 Beded Delux Tent', adults: 4, children: 2, pricePerNight: 2500 },
  { id: 'rt5', name: 'Double Beded Delux Tent', adults: 2, children: 1, pricePerNight: 1800 },
  { id: 'rt6', name: 'LUXURY TENT', adults: 2, children: 0, pricePerNight: 5000 },
  { id: 'rt7', name: '4 BEDED AC ROOM', adults: 4, children: 2, pricePerNight: 3000 },
  { id: 'rt8', name: 'Double Bed AC Round Cottage', adults: 2, children: 1, pricePerNight: 3500 },
  { id: 'rt9', name: 'Delux Room', adults: 2, children: 1, pricePerNight: 2200 },
  { id: 'rt10', name: 'Deluxe Cottage', adults: 4, children: 2, pricePerNight: 4000 },
];

// ============ OTA Channels ============
export const mockOTAChannelsData: OTAChannel[] = [
  { id: 'ota1', name: 'Booking.com', code: 'BOOKING', commission: 15, description: 'Leading global OTA with 28M+ listings' },
  { id: 'ota2', name: 'Expedia', code: 'EXPEDIA', commission: 18, description: 'Major US-based travel booking platform' },
  { id: 'ota3', name: 'Airbnb', code: 'AIRBNB', commission: 14, description: 'Global vacation rental marketplace' },
  { id: 'ota4', name: 'Agoda', code: 'AGODA', commission: 15, description: 'Asia-focused hotel booking platform' },
  { id: 'ota5', name: 'Hotels.com', code: 'HOTELS', commission: 17, description: 'Popular hotel booking website' },
  { id: 'ota6', name: 'MakeMyTrip', code: 'MMT', commission: 12, description: 'Indian travel booking platform' },
  { id: 'ota7', name: 'Goibibo', code: 'GOIBIBO', commission: 11, description: 'Indian travel and hotel booking' },
  { id: 'ota8', name: 'Direct Booking', code: 'DIRECT', commission: 0, description: 'Direct hotel website bookings' },
  { id: 'ota9', name: 'TripAdvisor', code: 'TRIPADVISOR', commission: 12, description: 'Travel review and booking platform' },
  { id: 'ota10', name: 'Trip.com', code: 'TRIP', commission: 14, description: 'Global online travel agency' },
];

// ============ Pricing Rules (organized by property ID) ============
export const mockPricingRulesData: Record<string, PricingRule[]> = {
  '1': [
    {
      id: 'rule-1',
      name: 'Last Minute Surge',
      priority: 1,
      isActive: true,
      conditions: [
        { type: 'daysUntilCheckIn', operator: 'lessThanOrEqual', value: 3, logic: 'AND' },
        { type: 'occupancy', operator: 'greaterThan', value: 70 }
      ],
      adjustmentType: 'percentage',
      adjustmentValue: 20,
      applyTo: 'basePrice',
      roomTypes: ['21'],
      otaChannels: ['ota1'],
      startDate: new Date().toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'rule-2',
      name: 'Weekend Premium',
      priority: 2,
      isActive: true,
      conditions: [
        { type: 'dayOfWeek', operator: 'equals', value: 5, logic: 'OR' },
        { type: 'dayOfWeek', operator: 'equals', value: 6 }
      ],
      adjustmentType: 'percentage',
      adjustmentValue: 15,
      applyTo: 'currentPrice',
      roomTypes: [],
      otaChannels: [],
      startDate: new Date().toISOString(),
      daysOfWeek: [5, 6],
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'rule-4',
      name: 'Festival Event Pricing',
      priority: 3,
      isActive: true,
      conditions: [
        { 
          type: 'event', 
          operator: 'equals', 
          value: 'event-1',
          eventDetails: {
            eventId: 'event-1',
            eventName: 'City Music Festival',
            startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
          }
        }
      ],
      adjustmentType: 'percentage',
      adjustmentValue: 30,
      applyTo: 'basePrice',
      roomTypes: [],
      otaChannels: [],
      startDate: new Date().toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  '2': [
    {
      id: 'rule-3',
      name: 'Early Bird Discount',
      priority: 1,
      isActive: true,
      conditions: [
        { type: 'daysUntilCheckIn', operator: 'greaterThan', value: 30, logic: 'AND' },
        { type: 'occupancy', operator: 'lessThan', value: 50 }
      ],
      adjustmentType: 'percentage',
      adjustmentValue: -10,
      applyTo: 'basePrice',
      roomTypes: [],
      otaChannels: [],
      startDate: new Date().toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'rule-5',
      name: 'Beach Marathon Event',
      priority: 2,
      isActive: true,
      conditions: [
        { 
          type: 'event', 
          operator: 'equals', 
          value: 'event-3',
          eventDetails: {
            eventId: 'event-3',
            eventName: 'Beach Marathon',
            startDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
          }
        }
      ],
      adjustmentType: 'percentage',
      adjustmentValue: 25,
      applyTo: 'currentPrice',
      roomTypes: [],
      otaChannels: [],
      startDate: new Date().toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  '3': [
    {
      id: 'rule-6',
      name: 'Low Season Discount',
      priority: 1,
      isActive: true,
      conditions: [
        { type: 'occupancy', operator: 'lessThan', value: 30 }
      ],
      adjustmentType: 'percentage',
      adjustmentValue: -15,
      applyTo: 'basePrice',
      roomTypes: [],
      otaChannels: [],
      startDate: new Date().toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
};

// ============ Availability Rules (organized by property ID) ============
export const mockAvailabilityRulesData: Record<string, AvailabilityRule[]> = {
  '1': [
    {
      id: 'avail-1',
      name: 'Weekend Min Stay',
      priority: 1,
      isActive: true,
      description: 'Minimum 2 nights on weekends',
      roomTypes: [],
      otaChannels: [],
      actionType: 'setMinStay',
      actionValue: 2,
      startDate: new Date().toISOString(),
      daysOfWeek: [5, 6],
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'avail-4',
      name: 'Festival Max Stay',
      priority: 2,
      isActive: true,
      description: 'Maximum 5 nights during festival',
      roomTypes: ['21'],
      otaChannels: ['ota1', 'ota2'],
      actionType: 'setMaxStay',
      actionValue: 5,
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  '2': [
    {
      id: 'avail-2',
      name: 'Peak Season Closure',
      priority: 1,
      isActive: true,
      description: 'Close to arrivals during peak',
      roomTypes: ['19'],
      otaChannels: ['ota1', 'ota2'],
      actionType: 'setStatus',
      actionValue: 'Closed to Arrival',
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'avail-5',
      name: 'Marathon Min Stay',
      priority: 2,
      isActive: true,
      description: 'Minimum 3 nights during marathon event',
      roomTypes: [],
      otaChannels: [],
      actionType: 'setMinStay',
      actionValue: 3,
      startDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(Date.now() + 16 * 24 * 60 * 60 * 1000).toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  '3': [
    {
      id: 'avail-3',
      name: 'Low Season Open',
      priority: 1,
      isActive: true,
      description: 'Keep all rooms open during low season',
      roomTypes: [],
      otaChannels: [],
      actionType: 'setStatus',
      actionValue: 'Open',
      startDate: new Date().toISOString(),
      processingFrequency: 'daily',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
};

// ============ Events (organized by property ID) ============
export const mockEventsData: Record<string, Event[]> = {
  '1': [
    {
      id: 'event-1',
      org_id: '1',
      name: 'City Music Festival',
      startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
      category: 'festival',
      impact: 'high',
      description: 'Annual city music festival with major artists',
      pricingMultiplier: 1.5,
      propertyIds: ['1'],
    },
    {
      id: 'event-2',
      org_id: '1',
      name: 'Tech Conference 2025',
      startDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(Date.now() + 23 * 24 * 60 * 60 * 1000).toISOString(),
      category: 'conference',
      impact: 'medium',
      description: 'Major tech industry conference',
      pricingMultiplier: 1.25,
      propertyIds: ['1', '2'],
    },
  ],
  '2': [
    {
      id: 'event-3',
      org_id: '1',
      name: 'Beach Marathon',
      startDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
      category: 'sports_event',
      impact: 'medium',
      description: 'Annual beach marathon event',
      pricingMultiplier: 1.2,
      propertyIds: ['2'],
    },
    {
      id: 'event-4',
      org_id: '1',
      name: 'Summer Holiday Week',
      startDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(Date.now() + 37 * 24 * 60 * 60 * 1000).toISOString(),
      category: 'holiday',
      impact: 'high',
      description: 'Peak summer holiday period',
      pricingMultiplier: 1.4,
      propertyIds: ['2', '3'],
    },
  ],
  '3': [
    {
      id: 'event-5',
      org_id: '1',
      name: 'Mountain Hiking Festival',
      startDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: new Date(Date.now() + 24 * 24 * 60 * 60 * 1000).toISOString(),
      category: 'sports_event',
      impact: 'medium',
      description: 'Annual hiking and outdoor activities festival',
      pricingMultiplier: 1.3,
      propertyIds: ['3'],
    },
  ],
};

// Legacy exports for backwards compatibility
export const mockProperties = mockPropertiesData;
export const mockOTAChannels = mockOTAChannelsData;
export const mockPricingRules: PricingRule[] = [];
export const mockEvents: Event[] = [];
